/**
 * Deterministic user-avatar helpers (Slice 1, 2026-02-14).
 *
 * A privacy-minimal avatar system for accounts that don't have an
 * external profile picture (email/password sign-ups, or Google users
 * whose picture failed to load).  No image upload, no image storage,
 * no external service (no Gravatar), no new database field.
 *
 * `computeInitials(name, email)`
 *   Returns 1–2 uppercase letters derived from the display name.
 *   Falls back to the first character of the email when no name is
 *   available.  The full email is never exposed by the avatar itself.
 *
 * `deterministicAvatarColor(seed)`
 *   Returns a `{ background, foreground }` pair chosen deterministically
 *   from a fixed Stockaily-compatible palette using a stable string
 *   hash so the same user always receives the same appearance across
 *   sessions and devices.  The palette is intentionally muted and
 *   fintech-neutral (no red/warning tones for regular users).
 */

const PALETTE = [
  { background: "#3182CE", foreground: "#FFFFFF" }, // Signature blue
  { background: "#3FAE89", foreground: "#0A0A0C" }, // Emerald
  { background: "#D9A441", foreground: "#0A0A0C" }, // Amber
  { background: "#9B7BFF", foreground: "#FFFFFF" }, // Soft violet
  { background: "#3DEBFF", foreground: "#0A0A0C" }, // Cyan
  { background: "#A47864", foreground: "#FFFFFF" }, // Warm sepia
  { background: "#5B7DB1", foreground: "#FFFFFF" }, // Muted azure
  { background: "#6EA88F", foreground: "#0A0A0C" }, // Sage
];

function stringHash(input) {
  const s = String(input || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    // 31-multiplier polynomial hash (Java-style) — fine for palette selection.
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function computeInitials(name, email) {
  const cleanedName = typeof name === "string" ? name.trim() : "";
  if (cleanedName) {
    const parts = cleanedName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  }
  const cleanedEmail = typeof email === "string" ? email.trim() : "";
  if (cleanedEmail) {
    const local = cleanedEmail.split("@")[0] || "";
    return (local[0] || "S").toUpperCase();
  }
  return "S";
}

export function deterministicAvatarColor(seed) {
  const idx = stringHash(seed) % PALETTE.length;
  return PALETTE[idx];
}

/**
 * `deriveAvatarProps(user)` — one-stop helper returning both the
 * initials and the deterministic color pair.  The seed strongly
 * prefers a stable identifier (`user_id`) so the color follows the
 * account through email/name changes.
 */
export function deriveAvatarProps(user) {
  const name = user?.name || "";
  const email = user?.email || "";
  const seed = user?.user_id || user?.id || email || name || "stockaily";
  return {
    initials: computeInitials(name, email),
    color: deterministicAvatarColor(seed),
  };
}
